import { APP_STRINGS } from '../constants';
import { getBoardLabels } from './boardUtils';

/** Project enabled in Integration (selected and not hidden). */
export const isIntegratedProject = (project) =>
  Boolean(project && project.isSelected && project.hideStatus !== true);

export const filterIntegratedProjects = (projectList) =>
  Array.isArray(projectList) ? projectList.filter(isIntegratedProject) : [];

/** First Integration-selected project, or null when none are selected. */
export const pickDefaultIntegratedProject = (projectList) =>
  filterIntegratedProjects(projectList)[0] || null;

/**
 * Default project for login / Standup bootstrap.
 * Prefer Integration-selected; if none, fall back to first visible (same as API login).
 * Treat missing hideStatus as visible (server docs often omit the field).
 */
export const pickDefaultProjectForBootstrap = (projectList) => {
  if (!Array.isArray(projectList) || projectList.length === 0) return null;
  return (
    pickDefaultIntegratedProject(projectList) ||
    projectList.find((p) => p && p.hideStatus !== true) ||
    projectList[0] ||
    null
  );
};

/** Prefer stored project when still integrated; otherwise first integrated project. */
export const resolveIntegratedProject = (projectList, storedProjectId = null) => {
  const integrated = filterIntegratedProjects(projectList);
  if (integrated.length === 0) {
    // Keep login/session project when Integration flags are missing but list has projects
    if (storedProjectId != null && storedProjectId !== '') {
      const stored = (projectList || []).find((p) => String(p._id) === String(storedProjectId));
      if (stored) return stored;
    }
    return pickDefaultProjectForBootstrap(projectList);
  }
  if (storedProjectId != null && storedProjectId !== '') {
    const stored = integrated.find((p) => String(p._id) === String(storedProjectId));
    if (stored) return stored;
  }
  return integrated[0];
};

export const isSimpleBoardType = (boardType) =>
  String(boardType || '').toLowerCase() === 'simple';

export const hasActiveOrCurrentSprint = (sprintList) =>
  Array.isArray(sprintList) &&
  sprintList.some((s) => {
    const state = String(s?.state || '').toLowerCase();
    return state === 'active' || state === 'current';
  });

export const shouldDefaultToRelease = ({ boardType, sprintList, releaseList }) => {
  const boardTypeKey = String(boardType || '').toLowerCase();
  const isKanbanOrGitLabBoard = ['kanban', 'gitlab-board'].includes(boardTypeKey);
  const hasNoSprints = !Array.isArray(sprintList) || sprintList.length === 0;
  const hasReleases = Array.isArray(releaseList) && releaseList.length > 0;
  if (!hasReleases) return false;
  if (isKanbanOrGitLabBoard) return true;
  if (hasNoSprints) return true;
  // Scrum / simple / other boards that have sprints: prefer sprint (active or latest).
  // Do not force Release just because no sprint is marked "active"/"current".
  return false;
};

export const pickDefaultRelease = (releaseList, storedReleaseId = null) => {
  if (!Array.isArray(releaseList) || releaseList.length === 0) return null;
  const stored =
    storedReleaseId != null
      ? releaseList.find((r) => String(r?._id) === String(storedReleaseId))
      : null;
  const latestUnreleased = releaseList
    .filter((r) => String(r?.status || '').toLowerCase() === 'unreleased')
    .sort((a, b) => new Date(b?.releaseDate || 0) - new Date(a?.releaseDate || 0))[0];
  const latestAny = [...releaseList].sort(
    (a, b) => new Date(b?.releaseDate || 0) - new Date(a?.releaseDate || 0),
  )[0];
  return stored || latestUnreleased || latestAny || null;
};

export const pickDefaultSprint = (sprintList, { boardType, storedSprintId } = {}) => {
  if (!Array.isArray(sprintList) || sprintList.length === 0) return null;
  const boardTypeKey = String(boardType || '').toLowerCase();
  const isClickupBoard = boardTypeKey.includes(APP_STRINGS.CLICKUP_LIST_KEBAB);
  if (isClickupBoard) {
    return (
      sprintList.find((s) => String(s?._id) === String(storedSprintId)) ||
      [...sprintList].sort((a, b) => new Date(b.endDate || 0) - new Date(a.endDate || 0))[0]
    );
  }
  return (
    sprintList.find((s) => s.state === 'active' || s.state === 'current') ||
    [...sprintList].sort((a, b) => new Date(b.endDate) - new Date(a.endDate))[0]
  );
};

/**
 * Resolve whether to default to sprint or release and which item to select.
 * Mirrors handleProject auto-selection logic.
 */
export function resolveDefaultSprintOrRelease({
  boardType,
  sprintList = [],
  releaseList = [],
  storedSprintId = null,
  storedReleaseId = null,
} = {}) {
  const { sprintLabel, releaseLabel } = getBoardLabels({ boardType });
  const useRelease = shouldDefaultToRelease({ boardType, sprintList, releaseList });

  if (useRelease) {
    const release = pickDefaultRelease(releaseList, storedReleaseId);
    if (!release) return { mode: null, sprint: null, release: null, sprintLabel, releaseLabel };
    return { mode: 'release', sprint: null, release, sprintLabel, releaseLabel };
  }

  const sprint = pickDefaultSprint(sprintList, { boardType, storedSprintId });
  if (!sprint) return { mode: null, sprint: null, release: null, sprintLabel, releaseLabel };
  return { mode: 'sprint', sprint, release: null, sprintLabel, releaseLabel };
}

/** Write default sprint/release + type labels to sessionStorage (no Redux). */
export function applyDefaultSelectionToSession({
  boardType,
  sprintList = [],
  releaseList = [],
  repoList = [],
} = {}) {
  const selection = resolveDefaultSprintOrRelease({ boardType, sprintList, releaseList });
  if (selection.mode === 'release' && selection.release) {
    sessionStorage.setItem('releaseId', selection.release._id);
    sessionStorage.setItem('releaseName', selection.release.releaseName);
    sessionStorage.setItem(APP_STRINGS.SESSION_TYPE_VALUE, APP_STRINGS.VALUE_RELEASE);
    sessionStorage.setItem(APP_STRINGS.SESSION_TYPE_VALUE_LABEL, selection.releaseLabel);
    sessionStorage.removeItem('sprintId');
    sessionStorage.removeItem('sprintName');
  } else if (selection.mode === 'sprint' && selection.sprint) {
    sessionStorage.setItem('sprintId', selection.sprint._id);
    sessionStorage.setItem('sprintName', selection.sprint.name);
    sessionStorage.setItem(APP_STRINGS.SESSION_TYPE_VALUE, APP_STRINGS.VALUE_SPRINT);
    sessionStorage.setItem(APP_STRINGS.SESSION_TYPE_VALUE_LABEL, selection.sprintLabel);
    sessionStorage.removeItem('releaseId');
    sessionStorage.removeItem('releaseName');
  }

  if (Array.isArray(repoList) && repoList.length > 0) {
    const defaultRepo = [...repoList].sort()[0];
    sessionStorage.setItem('repo', defaultRepo);
  }

  return selection;
}
